import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePublishToEmma } from '../hooks/usePublishToEmma';
import portalT from '../translations/portal';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import {
  ArrowLeft, Upload, TrendingUp, TrendingDown, AlertTriangle, 
  DollarSign, Calendar, Loader, Send, Plus, Lightbulb, 
  PiggyBank, ArrowUpRight, ArrowDownRight, Activity, Link2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api/compass/cashflow`;

const CashFlowPredictor = () => {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const t = portalT[lang]?.common || portalT.en.common;
  
  const [view, setView] = useState('start'); // start, analysis
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [description, setDescription] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [quickCheck, setQuickCheck] = useState(null);
  const [acctAvailable, setAcctAvailable] = useState(false);
  const [acctLoading, setAcctLoading] = useState(false);
  
  usePublishToEmma('AI Cashflow Predictor', analysis);
  
  const fileRef = useRef(null);
  const chatEndRef = useRef(null);

  const txt = {
    nl: {
      title: 'Cash Flow Predictor',
      subtitle: 'Voorspel je cashflow en voorkom liquiditeitsproblemen',
      uploadTitle: 'Upload Boekhouddata',
      uploadDesc: 'Upload je boekhoudexport (CSV/Excel) voor nauwkeurige voorspellingen',
      uploadBtn: 'Upload CSV/Excel',
      orDescribe: '— of beschrijf je situatie —',
      descPlaceholder: 'Bijv: Maandomzet €50.000, maandelijkse kosten €42.000, seizoenspieken in december. Grote factuur van €15.000 verwacht over 2 maanden...',
      analyzeBtn: 'Voorspel Cashflow',
      quickCheck: 'Snelle Check',
      quickCheckDesc: 'Krijg direct een cashflow gezondheidscheck',
      suggestions: [
        'Wanneer krijg ik cashflow problemen?',
        'Wat zijn mijn grootste uitgaven?',
        'Hoe verbeter ik mijn cashpositie?',
        'Voorspel de komende 6 maanden'
      ],
      chatPlaceholder: 'Stel een vraag over je cashflow...',
      newAnalysis: 'Nieuwe Analyse',
      warning: 'Waarschuwing',
      healthy: 'Gezond',
      caution: 'Let Op',
      critical: 'Kritiek',
    },
    en: {
      title: 'Cash Flow Predictor',
      subtitle: 'Predict your cashflow and prevent liquidity problems',
      uploadTitle: 'Upload Bookkeeping Data',
      uploadDesc: 'Upload your bookkeeping export (CSV/Excel) for accurate predictions',
      uploadBtn: 'Upload CSV/Excel',
      orDescribe: '— or describe your situation —',
      descPlaceholder: 'E.g.: Monthly revenue €50,000, monthly costs €42,000, seasonal peaks in December. Large invoice of €15,000 expected in 2 months...',
      analyzeBtn: 'Predict Cashflow',
      quickCheck: 'Quick Check',
      quickCheckDesc: 'Get an instant cashflow health check',
      suggestions: [
        'When will I have cashflow problems?',
        'What are my biggest expenses?',
        'How can I improve my cash position?',
        'Predict the next 6 months'
      ],
      chatPlaceholder: 'Ask a question about your cashflow...',
      newAnalysis: 'New Analysis',
      warning: 'Warning',
      healthy: 'Healthy',
      caution: 'Caution',
      critical: 'Critical',
    }
  }[lang] || {
    nl: {
      title: 'Cash Flow Predictor',
      subtitle: 'Voorspel je cashflow',
      uploadTitle: 'Upload Data',
      uploadDesc: 'Upload boekhouddata',
      uploadBtn: 'Upload',
      orDescribe: 'of beschrijf',
      descPlaceholder: 'Beschrijf...',
      analyzeBtn: 'Analyseer',
      quickCheck: 'Snelle Check',
      quickCheckDesc: 'Direct check',
      suggestions: [],
      chatPlaceholder: 'Vraag...',
      newAnalysis: 'Nieuw',
      warning: 'Waarschuwing',
      healthy: 'Gezond',
      caution: 'Let Op',
      critical: 'Kritiek',
    }
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Check if accounting data is available
  useEffect(() => {
    const checkAcct = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/compass/cashflow/accounting-check`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAcctAvailable(!!res.data?.has_data);
      } catch { /* ignore */ }
    };
    checkAcct();
  }, [token]);

  const loadFromAccounting = async () => {
    setAcctLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/compass/cashflow/load-accounting`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessionId(res.data.session_id);
      setUploadedFile({
        filename: res.data.filename,
        rows: res.data.summary?.invoices_total || 0,
        columns: ['datum', 'type', 'categorie', 'bedrag', 'status'],
        source: 'accounting',
        summary: res.data.summary,
      });
      setDescription(lang === 'nl'
        ? `Analyseer mijn cashflow op basis van gesynchroniseerde boekhouddata. Periode: ${res.data.summary?.period || 'onbekend'}. Totale omzet: EUR ${res.data.summary?.total_revenue?.toLocaleString('nl-NL') || 0}, totale kosten: EUR ${res.data.summary?.total_expenses?.toLocaleString('nl-NL') || 0}, huidig banksaldo: EUR ${res.data.summary?.bank_balance?.toLocaleString('nl-NL') || 0}.`
        : `Analyze my cashflow based on synced accounting data. Period: ${res.data.summary?.period || 'unknown'}. Total revenue: EUR ${res.data.summary?.total_revenue?.toLocaleString('en-US') || 0}, total expenses: EUR ${res.data.summary?.total_expenses?.toLocaleString('en-US') || 0}, current bank balance: EUR ${res.data.summary?.bank_balance?.toLocaleString('en-US') || 0}.`
      );
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to load accounting data');
    } finally {
      setAcctLoading(false);
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUploadedFile(res.data);
      setSessionId(res.data.session_id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const analyze = async () => {
    if (!((description) == null ? '' : String(description)).trim() && !uploadedFile) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/analyze`, {
        description: description || `Geüpload bestand: ${uploadedFile?.filename}`,
        session_id: sessionId,
        language: lang
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessionId(res.data.session_id);
      setAnalysis(res.data.analysis);
      setMessages([
        { role: 'user', content: description || 'Analyseer geüploade data' },
        { role: 'assistant', content: res.data.analysis }
      ]);
      setView('analysis');
    } catch (err) {
      alert(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const doQuickCheck = async () => {
    if (!((description) == null ? '' : String(description)).trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/quick-check`, {
        description,
        language: lang
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuickCheck(res.data.quick_check);
    } catch (err) {
      alert(err.response?.data?.detail || 'Quick check failed');
    } finally {
      setLoading(false);
    }
  };

  const sendChat = async (msg) => {
    if (!msg?.trim() || !sessionId) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: msg,
        language: lang
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: (lang === 'en' ? 'Error: ' : 'Fout: ') + (err.response?.data?.detail || (lang === 'en' ? 'Request failed' : 'Verzoek mislukt')) }]);
    } finally {
      setLoading(false);
    }
  };

  const startNew = () => {
    setView('start');
    setSessionId(null);
    setUploadedFile(null);
    setDescription('');
    setAnalysis(null);
    setMessages([]);
    setQuickCheck(null);
  };

  // Start view
  if (view === 'start') {
    return (
      <div className="tool-page">
        <div className="tool-header">
          <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary">
            <ArrowLeft size={16} /> {t.aiTools}
          </button>
          <h1 className="heading-1">
            <PiggyBank size={22} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} />
            {txt.title}
          </h1>
        </div>
        
        <div className="tool-content">
          <p className="body-medium text-secondary" style={{ marginBottom: '2rem', maxWidth: '600px' }}>
            {txt.subtitle}
          </p>
          
          <div className="auto-start-section" data-testid="cashflow-start">
            {/* Upload section */}
            <div className="auto-input-card" data-testid="upload-card">
              <div className="auto-input-header">
                <Upload size={20} />
                <h3 className="heading-3">{txt.uploadTitle}</h3>
              </div>
              <p className="body-small text-secondary">{txt.uploadDesc}</p>

              {/* Load from Accounting button */}
              {acctAvailable && !uploadedFile && (
                <button
                  className="btn-primary"
                  style={{ width: '100%', margin: '0.75rem 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                  onClick={loadFromAccounting}
                  disabled={acctLoading}
                  data-testid="load-accounting-btn"
                >
                  {acctLoading ? <Loader className="spin" size={16} /> : <Link2 size={16} />}
                  {lang === 'nl' ? 'Laden vanuit Boekhoudsoftware' : 'Load from Accounting Software'}
                </button>
              )}
              
              <div 
                className="explorer-dropzone" 
                style={{ margin: '1rem 0', padding: '1.5rem', cursor: 'pointer' }}
                onClick={() => fileRef.current?.click()}
                data-testid="cashflow-dropzone"
              >
                {loading ? (
                  <><Loader className="spin" size={24} /><span className="body-small">Uploading...</span></>
                ) : uploadedFile ? (
                  <>
                    {uploadedFile.source === 'accounting' ? <Link2 size={24} style={{ color: '#22c55e' }} /> : <Activity size={24} style={{ color: 'var(--accent-green)' }} />}
                    <span className="body-small" style={{ fontWeight: 600 }}>{uploadedFile.filename}</span>
                    <span className="caption">
                      {uploadedFile.source === 'accounting'
                        ? `${uploadedFile.summary?.months_covered || 0} ${lang === 'nl' ? 'maanden' : 'months'} · ${uploadedFile.summary?.invoices_total || 0} ${lang === 'nl' ? 'facturen' : 'invoices'}`
                        : `${uploadedFile.rows} rows · ${uploadedFile.columns?.length} columns`}
                    </span>
                  </>
                ) : (
                  <>
                    <Upload size={24} />
                    <span className="body-small">{txt.uploadBtn}</span>
                  </>
                )}
                <input 
                  ref={fileRef} 
                  type="file" 
                  accept=".csv,.xlsx,.xls" 
                  style={{ display: 'none' }}
                  onChange={(e) => uploadFile(e.target.files[0])}
                />
              </div>
              
              <div style={{ textAlign: 'center', margin: '1rem 0', color: 'var(--text-secondary)' }}>
                {txt.orDescribe}
              </div>
              
              <textarea
                className="form-input auto-textarea"
                placeholder={txt.descPlaceholder}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                data-testid="cashflow-textarea"
              />
              
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                <button 
                  className="btn-primary" 
                  style={{ flex: 1 }}
                  onClick={analyze}
                  disabled={loading || (!((description) == null ? '' : String(description)).trim() && !uploadedFile)}
                  data-testid="cashflow-analyze-btn"
                >
                  {loading ? <Loader className="spin" size={16} /> : <TrendingUp size={16} />}
                  {txt.analyzeBtn}
                </button>
                <button 
                  className="btn-secondary"
                  onClick={doQuickCheck}
                  disabled={loading || !((description) == null ? '' : String(description)).trim()}
                  data-testid="quick-check-btn"
                >
                  <Activity size={16} /> {txt.quickCheck}
                </button>
              </div>
            </div>
            
            {/* Quick check result */}
            {quickCheck && (
              <div className="auto-input-card" style={{ background: 'var(--bg-section)' }} data-testid="quick-check-result">
                <h4 className="heading-3" style={{ marginBottom: '1rem' }}>
                  <Activity size={18} style={{ display: 'inline', marginRight: 8 }} />
                  {txt.quickCheck}
                </h4>
                <div className="markdown-content">
                  <ReactMarkdown>{quickCheck}</ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Analysis view
  return (
    <div className="compass-coach-page" data-testid="cashflow-workspace">
      <aside className="compass-sidebar">
        <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary" style={{ width: '100%', justifyContent: 'center' }}>
          <ArrowLeft size={14} /> {t.aiTools}
        </button>
        
        <div className="compass-role-badge" style={{ background: 'var(--accent-green-200)' }}>
          <PiggyBank size={16} />
          <span className="body-small" style={{ fontWeight: 500 }}>{txt.title}</span>
        </div>
        
        {uploadedFile && (
          <div className="sim-sidebar-stats" data-testid="file-stats">
            <div className="sim-stat-item">
              {uploadedFile.source === 'accounting' ? <Link2 size={14} /> : <Activity size={14} />}
              <span className="body-small">{uploadedFile.filename}</span>
            </div>
            <div className="sim-stat-item">
              <DollarSign size={14} />
              <span className="body-small">
                {uploadedFile.source === 'accounting'
                  ? `${uploadedFile.summary?.months_covered || 0} ${lang === 'nl' ? 'maanden data' : 'months data'}`
                  : `${uploadedFile.rows} ${lang === 'nl' ? 'rijen' : 'rows'}`}
              </span>
            </div>
          </div>
        )}
        
        <button className="compass-new-chat" onClick={startNew} data-testid="new-analysis-btn">
          <Plus size={14} /> {txt.newAnalysis}
        </button>
      </aside>
      
      <main className="compass-main">
        <div className="compass-chat-container">
          <div className="compass-chat-messages" data-testid="cashflow-messages">
            {(Array.isArray(messages) ? messages : []).map((msg, i) => (
              <div key={i} className={`compass-msg compass-msg-${msg.role}`}>
                {msg.role === 'assistant' ? (
                  <div className="compass-msg-content markdown-content">
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  </div>
                ) : (
                  <div className="compass-msg-content">{msg.content}</div>
                )}
              </div>
            ))}
            
            {loading && (
              <div className="compass-msg compass-msg-assistant">
                <div className="compass-msg-content compass-typing">
                  <Loader className="spin" size={16} /> {lang === 'nl' ? 'Analyseren...' : 'Analyzing...'}
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          
          {/* Suggestions */}
          {messages.length <= 2 && !loading && (
            <div className="auto-followup-bar" data-testid="cashflow-suggestions">
              {(Array.isArray(txt.suggestions) ? txt.suggestions : []).map((s, i) => (
                <button key={i} className="compass-suggestion-btn" onClick={() => sendChat(s)}>
                  <Lightbulb size={14} /> {s}
                </button>
              ))}
            </div>
          )}
          
          <form className="compass-chat-input" onSubmit={(e) => { e.preventDefault(); sendChat(input); }}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={txt.chatPlaceholder}
              disabled={loading}
              className="form-input"
              data-testid="cashflow-input"
            />
            <button type="submit" disabled={loading || !((input) == null ? '' : String(input)).trim()} className="btn-primary compass-send-btn">
              <Send size={16} />
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default CashFlowPredictor;
